import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import {
    Table,
    Button,
    Space,
    Card,
    Typography,
    message,
    Tag,
    Tooltip,
    Input,
    Select,
    Row,
    Col,
    Statistic,
    Spin,
    Modal,
    Form,
    Popconfirm,
    Dropdown,
    DatePicker,
    Avatar,
    Checkbox,
    Popover,
    Collapse,
    Empty
} from 'antd';
import {
    EyeOutlined,
    SearchOutlined,
    FilterOutlined,
    UserOutlined,
    ShoppingOutlined,
    CalendarOutlined,
    PlayCircleOutlined,
    StopOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    MoreOutlined,
    TrophyOutlined,
    CrownOutlined,
    FilePdfOutlined,
    ReloadOutlined,
    SettingOutlined,
    StarFilled,
    StarOutlined,
    DeleteOutlined,
    UndoOutlined
} from '@ant-design/icons';
import moment from 'moment';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import useColumnVisibility from '../Common/ColumnVisibilityControl';
import { getAuctionStartTimeStatus } from '../../utils/dateUtils';

// Add CSS for scheduled auction row highlighting
const styles = `
    .scheduled-auction-row {
        background-color: #fff1f0 !important;
        border-left: 4px solid #ff4d4f !important;
    }
    .scheduled-auction-row:hover {
        background-color: #fff1f0 !important;
        border-left: 4px solid #ff4d4f !important;
    }
    @media print {
        .no-print {
            display: none !important;
        }
        .print-only {
            display: block !important;
        }
        .cover-page {
            page-break-after: always;
            height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 20mm 40mm;
            text-align: center;
            box-sizing: border-box;
            background: linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 50%, #ffffff 100%);
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        .lots-content {
            page-break-before: always;
        }
        .disclaimer-page {
            page-break-before: always;
            min-height: 200vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            padding: 25mm 35mm;
            text-align: center;
            box-sizing: border-box;
            background: white;
        }
        .ant-card {
            break-inside: avoid;
            page-break-inside: avoid;
        }
    }
    .print-only {
        display: none;
    }
`;

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const AdminAuctionManagement = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0
    });
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        category: 'all',
        user_id: '',
        created_at_range: null,
        start_time_range: null,
        is_featured: 'all'
    });
    const [meta, setMeta] = useState({
        total_auctions: 0,
        active_auctions: 0,
        scheduled_auctions: 0,
        completed_auctions: 0
    });
    const [statusModalVisible, setStatusModalVisible] = useState(false);
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);
    const [lastBiddersModalVisible, setLastBiddersModalVisible] = useState(false);
    const [lastBiddersLoading, setLastBiddersLoading] = useState(false);
    const [lastBiddersData, setLastBiddersData] = useState(null);
    const [selectedLotIds, setSelectedLotIds] = useState(new Set());
    const [filterDropdownVisible, setFilterDropdownVisible] = useState(false);
    const [lotsModalVisible, setLotsModalVisible] = useState(false);
    const [selectedAuctionForLots, setSelectedAuctionForLots] = useState(null);
    const [auctionLots, setAuctionLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(false);
    const [loadingLotId, setLoadingLotId] = useState(null);
    const [form] = Form.useForm();
    const [searchForm] = Form.useForm();
    const navigate = useNavigate();
    const printRef = useRef();
    const { currentUser } = useSelector((state) => state.user);


    const formatCurrency = (value) => {
        try {
            return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }).format(Number(value || 0));
        } catch (e) {
            return `₨${Number(value || 0).toLocaleString('en-PK')}`;
        }
    };

    const getWinnerLabel = (index) => {
        if (index === 0) return 'First winner';
        if (index === 1) return 'Second last winner';
        if (index === 2) return 'Third last winner';
        return '';
    };

    const getLotStatusTag = (status) => {
        if (!status) return null;
        
        const statusConfig = {
            'closed': { color: 'green', text: 'Sold' },
            'cancelled': { color: 'red', text: 'Cancelled - Reserve Not Met' },
            'timeout': { color: 'orange', text: 'Timeout - No Bids' }
        };

        const config = statusConfig[status.toLowerCase()];
        if (!config) return null;

        return (
            <Tag color={config.color} style={{ margin: 0 }}>
                {config.text}
            </Tag>
        );
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Last 3 Bidders - ${lastBiddersData?.auction?.title || 'Auction'}`,
        onBeforeGetContent: () => {
            if (!printRef.current) {
                message.error('Content not ready for printing');
                return Promise.resolve();
            }
            return Promise.resolve();
        },
        pageStyle: `
            @page {
                size: A4 landscape;
                margin: 20mm;
            }
            @media print {
                .no-print {
                    display: none !important;
                }
                .print-only {
                    display: block !important;
                }
                body {
                    background: white !important;
                }
            }
        `
    });

    useEffect(() => {
        loadAuctions();
    }, [pagination.current, pagination.pageSize, filters]);

    const loadAuctions = async () => {
        try {
            setLoading(true);
            setStatisticsLoading(true);
            const params = {
                page: pagination.current,
                per_page: pagination.pageSize,
                ...filters
            };

            // Convert date ranges to ISO strings for API
            if (filters.created_at_range && filters.created_at_range.length === 2) {
                params.created_at_from = filters.created_at_range[0].startOf('day').toISOString();
                params.created_at_to = filters.created_at_range[1].endOf('day').toISOString();
                delete params.created_at_range;
            }

            if (filters.start_time_range && filters.start_time_range.length === 2) {
                params.start_time_from = filters.start_time_range[0].startOf('day').toISOString();
                params.start_time_to = filters.start_time_range[1].endOf('day').toISOString();
                delete params.start_time_range;
            }

            const response = await auctionService.getAdminAuctions(params);
            
            if (response.data) {
                setAuctions(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.meta.total_auctions
                }));
                setMeta(response.meta || {});
            }
        } catch (error) {
            console.error('Error loading auctions:', error);
            message.error('Failed to load auctions');
        } finally {
            setLoading(false);
            setStatisticsLoading(false);
        }
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const handleSearch = (values) => {
        const newFilters = {
            search: values.search || '',
            status: values.status || 'all',
            category: values.category || 'all',
            user_id: values.user_id || '',
            created_at_range: values.created_at_range || null,
            start_time_range: values.start_time_range || null,
            is_featured: values.is_featured || 'all'
        };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleResetFilters = () => {
        searchForm.resetFields();
        const defaultFilters = {
            search: '',
            status: 'all',
            category: 'all',
            user_id: '',
            created_at_range: null,
            start_time_range: null,
            is_featured: 'all'
        };
        setFilters(defaultFilters);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleStatusChange = async (auction, newStatus) => {
        try {
            setStatusChangeLoading(true);
            
            const response = await auctionService.updateAdminAuctionStatus(auction.slug, newStatus);

            if (response.status === 'success') {
                message.success(response.message || `Auction status changed to ${newStatus} successfully`);
                loadAuctions(); // Refresh the list
                setStatusModalVisible(false);
                setSelectedAuction(null);
                form.resetFields();
            } else {
                message.error(response.message || 'Failed to change auction status');
            }
        } catch (error) {
            console.error('Error changing auction status:', error);
            message.error(error.response?.data?.message || 'Failed to change auction status');
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleFeaturedToggle = async (auction) => {
        try {
            setStatusChangeLoading(true);
            
            const response = await auctionService.updateAdminAuctionStatus(auction.slug, auction.status, {
                is_featured: !auction.is_featured
            });

            if (response.status === 'success') {
                message.success(`Auction ${!auction.is_featured ? 'featured' : 'unfeatured'} successfully`);
                loadAuctions(); // Refresh the list
            } else {
                message.error(response.message || 'Failed to update featured status');
            }
        } catch (error) {
            console.error('Error updating featured status:', error);
            message.error(error.response?.data?.message || 'Failed to update featured status');
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const showStatusModal = (auction) => {
        setSelectedAuction(auction);
        form.setFieldsValue({ 
            status: auction.status,
            is_featured: auction.is_featured
        });
        setStatusModalVisible(true);
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            scheduled: 'processing',
            active: 'success',
            completed: 'success',
            cancelled: 'error',
            pending: 'default',
            closed: 'success',
            timeout: 'warning'
        };
        return colors[status] || 'default';
    };

    const handleDeleteAuction = (auction) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this auction?',
            icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div>
                    <p>This action cannot be undone. All lots associated with this auction will also be affected.</p>
                    <p>Auction: <strong>{auction.title}</strong></p>
                </div>
            ),
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'No, Keep it',
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await auctionService.deleteAdminAuction(auction.slug);
                    if (response.status === 'success') {
                        message.success(response.message || 'Auction deleted successfully');
                        loadAuctions(); // Refresh the list
                    } else {
                        message.error(response.message || 'Failed to delete auction');
                    }
                } catch (error) {
                    console.error('Error deleting auction:', error);
                    message.error(error.response?.data?.message || 'Failed to delete auction');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleResetAuction = (auction) => {
        Modal.confirm({
            title: 'Reset this auction?',
            icon: <UndoOutlined style={{ color: '#fa8c16' }} />,
            content: (
                <div>
                    <p>
                        This will permanently delete all bids and bid-related data for this auction,
                        reopen every lot, and make the auction live again.
                    </p>
                    <p>Auction: <strong>{auction.title}</strong></p>
                    <p style={{ color: '#ff4d4f', marginBottom: 0 }}>This action cannot be undone.</p>
                </div>
            ),
            okText: 'Yes, Reset Auction',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await auctionService.resetAdminAuction(auction.slug);
                    if (response.status === 'success') {
                        message.success(response.message || 'Auction reset successfully');
                        loadAuctions();
                    } else {
                        message.error(response.message || 'Failed to reset auction');
                    }
                } catch (error) {
                    console.error('Error resetting auction:', error);
                    message.error(error.response?.data?.message || 'Failed to reset auction');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const getStatusActions = (auction) => {
        const items = [
            {
                key: 'view',
                label: 'View Auction',
                icon: <EyeOutlined />,
                onClick: () => navigate(`/auctions/${auction.slug}`)
            },
            {
                type: 'divider'
            }
        ];

        // Add featured toggle option
        items.push({
            key: 'toggle-featured',
            label: auction.is_featured ? 'Unfeature Auction' : 'Feature Auction',
            icon: auction.is_featured ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
            onClick: () => handleFeaturedToggle(auction)
        });

        // Add status-specific actions
        switch (auction.status) {
            case 'scheduled':
                items.push({
                    key: 'start',
                    label: 'Start Auction',
                    icon: <PlayCircleOutlined />,
                    onClick: () => handleStatusChange(auction, 'active')
                });
                break;
            case 'active':
                items.push({
                    key: 'end',
                    label: 'End Auction',
                    icon: <StopOutlined />,
                    onClick: () => handleStatusChange(auction, 'completed')
                });
                break;
            case 'completed':
                items.push({
                    key: 'last-bidders',
                    label: 'View Last 3 Bidders (per lot)',
                    icon: <UserOutlined />,
                    onClick: () => openLastBiddersModal(auction)
                });
                break;
        }

        items.push({
            key: 'reset-auction',
            label: <span style={{ color: '#fa8c16' }}>Reset Auction</span>,
            icon: <UndoOutlined style={{ color: '#fa8c16' }} />,
            onClick: () => handleResetAuction(auction)
        });

        // Add general status change option
        items.push({
            key: 'change-status',
            label: 'Change Status',
            icon: <EditOutlined />,
            onClick: () => showStatusModal(auction)
        });

        items.push({
            type: 'divider'
        });

        items.push({
            key: 'delete',
            label: <span style={{ color: '#ff4d4f' }}>Delete Auction</span>,
            icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
            onClick: () => handleDeleteAuction(auction)
        });

        return items;
    };

    const openLastBiddersModal = async (auction) => {
        try {
            setLastBiddersLoading(true);
            setLastBiddersModalVisible(true);
            const response = await auctionService.getAdminAuctionLastBidders(auction.slug);
            setLastBiddersData(response);
            // Initialize all lots as selected
            const allLotIds = new Set(response.lots?.map(lot => lot.id) || []);
            setSelectedLotIds(allLotIds);
        } catch (error) {
            console.error('Error fetching last bidders:', error);
            message.error(error.response?.data?.message || 'Failed to load last bidders');
            setLastBiddersModalVisible(false);
        } finally {
            setLastBiddersLoading(false);
        }
    };

    const toggleLotSelection = (lotId) => {
        setSelectedLotIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(lotId)) {
                newSet.delete(lotId);
            } else {
                newSet.add(lotId);
            }
            return newSet;
        });
    };

    const selectAllLots = () => {
        if (!lastBiddersData) return;
        const allLotIds = new Set(lastBiddersData.lots?.map(lot => lot.id) || []);
        setSelectedLotIds(allLotIds);
    };

    const deselectAllLots = () => {
        setSelectedLotIds(new Set());
    };

    const openLotsModal = async (auction) => {
        try {
            setSelectedAuctionForLots(auction);
            setLotsModalVisible(true);
            setLotsLoading(true);
            const response = await auctionService.getAdminAuctionLots(auction.slug);
            setAuctionLots(response.data || []);
        } catch (error) {
            console.error('Error fetching auction lots:', error);
            message.error('Failed to load lots');
        } finally {
            setLotsLoading(false);
        }
    };

    const handleToggleLotFeatured = async (auction, lot) => {
        try {
            setLoadingLotId(lot.id);
            const response = await auctionService.toggleLotFeatured(auction.slug, lot.slug);
            if (response.status === 'success') {
                message.success(response.message);
                // Update local state
                setAuctionLots(prevLots => 
                    prevLots.map(l => 
                        l.id === lot.id 
                            ? { ...l, is_featured: !l.is_featured }
                            : l
                    )
                );
            }
        } catch (error) {
            console.error('Error toggling lot featured status:', error);
            message.error(error.response?.data?.message || 'Failed to update featured status');
        } finally {
            setLoadingLotId(null);
        }
    };

    const getFilteredLots = () => {
        if (!lastBiddersData) return [];
        // If no lots selected, show all (but for print, use selected)
        if (selectedLotIds.size === 0) {
            return lastBiddersData.lots || [];
        }
        return lastBiddersData.lots?.filter(lot => selectedLotIds.has(lot.id)) || [];
    };

    const isCreatedToday = (date) => {
        return moment(date).isSame(moment(), 'day');
    };

    const baseColumns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 300,
            render: (text, record) => (
                <div>
                    <a 
                        href={`/auctions/${record.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                            fontWeight: 'bold',
                            color: '#1890ff',
                            textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                        }}
                    >
                        {text}
                    </a>
                    {/* <Text type="secondary" style={{ fontSize: '12px' }}>
                        {text}
                    </Text> */}
                </div>
            )
        },
        {
            title: 'Owner',
            dataIndex: 'user',
            key: 'user',
            width: 150,
            render: (user) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>
                        {user?.first_name} {user?.last_name}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {user?.email}
                    </Text>
                </div>
            )
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            render: (category) => (
                <Tag color="blue">
                    {category?.name || 'N/A'}
                </Tag>
            )
        },
        {
            title: 'Lots',
            dataIndex: 'lots_count',
            key: 'lots_count',
            width: 120,
            render: (count, record) => (
                <Button
                    type="default"
                    icon={<ShoppingOutlined />}
                    onClick={() => openLotsModal(record)}
                    style={{ 
                        borderRadius: '6px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    {count || '0'} Lots
                </Button>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 180,
            render: (date) => {
                const { statusText, statusColor, formattedDate, tooltipText } = getAuctionStartTimeStatus(date);
                
                return (
                    <Tooltip title={tooltipText}>
                <div>
                        <div style={{ marginBottom: 2 }}>
                                {formattedDate}
                </div>
                        {statusText && (
                            <Tag color={statusColor} size="small" style={{ marginTop: 4 }}>
                                {statusText}
                            </Tag>
                        )}
                    </div>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status, record) => (
                <div>
                <Tag color={getStatusColor(status)}>
                    {status.toUpperCase()}
                </Tag>
                    {status === 'scheduled' && (
                        <div style={{ marginTop: 4 }}>
                            <Tag color="red" size="small">
                                Needs attention
                            </Tag>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Type',
            key: 'type',
            width: 120,
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Tag color={record.is_private ? 'orange' : 'green'}>
                        {record.is_private ? 'PRIVATE' : 'PUBLIC'}
                    </Tag>
                    <Tag color={record.is_sequential ? 'blue' : 'purple'}>
                        {record.is_sequential ? 'SEQUENTIAL' : 'SIMULTANEOUS'}
                    </Tag>
                </Space>
            )
        },
        {
            title: 'Featured',
            dataIndex: 'is_featured',
            key: 'is_featured',
            width: 120,
            render: (isFeatured, record) => (
                <Button
                    type={isFeatured ? 'primary' : 'default'}
                    icon={isFeatured ? <StarFilled /> : <StarOutlined />}
                    onClick={() => handleFeaturedToggle(record)}
                    style={{ 
                        borderRadius: '6px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    {isFeatured ? 'Featured' : 'Regular'}
                </Button>
            )
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            width: 120,
            render: (location) => {
                if (!location) return 'N/A';
                const maxLength = 30;
                if (location.length > maxLength) {
                    return (
                        <Tooltip title={location}>
                            <span>{location.substring(0, maxLength)}...</span>
                        </Tooltip>
                    );
                }
                return location;
            }
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 120,
            render: (date) => {
                if (isCreatedToday(date)) {
                    return (
                        <Tag color="green" style={{ fontWeight: 'bold' }}>
                            Created Today
                        </Tag>
                    );
                }
                return moment(date).format('YYYY-MM-DD');
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Dropdown
                    menu={{ items: getStatusActions(record) }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                    />
                </Dropdown>
            )
        }
    ];

    // Column visibility control - must be defined after baseColumns
    const { visibleColumns, ColumnVisibilityUI } = useColumnVisibility({
        baseColumns,
        storageKey: 'adminAuctionColumnsVisibility'
    });
    const columns = visibleColumns;

    return (
        <DashboardLayout>
            <style>{styles}</style>
            <PageHeader
                title="Admin Auction Management"
                description="Monitor and manage all auctions across the platform"
            />

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Total Auctions"
                                value={meta.total_auctions || 0}
                                valueStyle={{ color: '#1890ff' }}
                                prefix={<ShoppingOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Active Auctions"
                                value={meta.active_auctions || 0}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<ShoppingOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Scheduled Auctions"
                                value={meta.scheduled_auctions || 0}
                                valueStyle={{ color: '#faad14' }}
                                prefix={<CalendarOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Completed Auctions"
                                value={meta.completed_auctions || 0}
                                valueStyle={{ color: '#722ed1' }}
                                prefix={<ShoppingOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
            </Row>

            {/* Advanced Search */}
            <Card style={{ marginBottom: 16 }}>
                <Collapse defaultActiveKey={['1']} ghost>
                    <Panel 
                        header={
                            <Space>
                                <FilterOutlined />
                                <span>Advanced Search</span>
                            </Space>
                        } 
                        key="1"
                    >
                        <Form
                            form={searchForm}
                            layout="vertical"
                            onFinish={handleSearch}
                            initialValues={{
                                search: '',
                                status: 'all',
                                category: 'all',
                                is_featured: 'all',
                                created_at_range: null,
                                start_time_range: null
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="search"
                                        label="Search"
                                    >
                                        <Input 
                                            prefix={<SearchOutlined />}
                            placeholder="Search auctions, users, or categories..."
                        />
                                    </Form.Item>
                    </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="status"
                                        label="Status"
                                    >
                                        <Select placeholder="Select Status">
                            <Option value="all">All Status</Option>
                            <Option value="active">Active</Option>
                            <Option value="scheduled">Scheduled</Option>
                            <Option value="completed">Completed</Option>
                                            <Option value="cancelled">Cancelled</Option>
                                            {/* <Option value="draft">Draft</Option> */}
                        </Select>
                                    </Form.Item>
                    </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="is_featured"
                                        label="Featured"
                                    >
                                        <Select placeholder="Select Featured Status">
                            <Option value="all">All</Option>
                            <Option value="true">Featured</Option>
                            <Option value="false">Regular</Option>
                        </Select>
                                    </Form.Item>
                    </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="created_at_range"
                                        label="Created Date Range"
                                    >
                        <RangePicker
                            placeholder={['Created From', 'Created To']}
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                        />
                                    </Form.Item>
                    </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="start_time_range"
                                        label="Start Time Range"
                                    >
                        <RangePicker
                            placeholder={['Start From', 'Start To']}
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                        />
                                    </Form.Item>
                    </Col>
                            </Row>
                            <Form.Item>
                                <Space>
                        <Button
                                        type="primary" 
                                        htmlType="submit"
                                        icon={<SearchOutlined />}
                                    >
                                        Search
                        </Button>
                                    <Button 
                                        onClick={handleResetFilters}
                                        icon={<ReloadOutlined />}
                                    >
                                        Reset
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Panel>
                </Collapse>
            </Card>

            {/* Column Visibility Control */}
            {ColumnVisibilityUI}

            {/* Auctions Table */}
                <div style={{ overflowX: 'auto' }}>
                    <Table
                        columns={columns}
                        dataSource={auctions}
                        rowKey="id"
                        loading={loading}
                        rowClassName={(record) => {
                            if (record.status === 'scheduled') {
                                return 'scheduled-auction-row';
                            }
                            return '';
                        }}
                        pagination={{
                            ...pagination,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} auctions`
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1500 }}
                        size="middle"
                    />
                </div>

            {/* Status Change Modal */}
            <Modal
                title="Change Auction Status & Settings"
                open={statusModalVisible}
                onCancel={() => {
                    setStatusModalVisible(false);
                    setSelectedAuction(null);
                    form.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={(values) => handleStatusChange(selectedAuction, values.status, values)}
                >
                    <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select placeholder="Select new status">
                            <Option value="scheduled">Scheduled</Option>
                            <Option value="active">Active</Option>
                            <Option value="completed">Completed</Option>
                        </Select>
                    </Form.Item>

                    {/* <Form.Item
                        name="is_featured"
                        label="Featured Status"
                        valuePropName="checked"
                    >
                        <Select placeholder="Select featured status">
                            <Option value={true}>Featured</Option>
                            <Option value={false}>Regular</Option>
                        </Select>
                    </Form.Item> */}
                    
                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={statusChangeLoading}
                            >
                                Update Auction
                            </Button>
                            <Button
                                onClick={() => {
                                    setStatusModalVisible(false);
                                    setSelectedAuction(null);
                                    form.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Last Bidders Modal */}
            <Modal
                title={
                    lastBiddersData ? (
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
                                Last 3 Bidders
                            </div>
                            <div style={{ fontSize: 14, color: '#8c8c8c', fontWeight: 'normal' }}>
                                {lastBiddersData.auction?.title}
                            </div>
                        </div>
                    ) : 'Last 3 bidders'
                }
                open={lastBiddersModalVisible}
                onCancel={() => {
                    setLastBiddersModalVisible(false);
                    setLastBiddersData(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setLastBiddersModalVisible(false);
                        setLastBiddersData(null);
                        setSelectedLotIds(new Set());
                        setFilterDropdownVisible(false);
                    }}>
                        Close
                    </Button>,
                    <Popover
                        key="filter"
                        content={
                            <div style={{ 
                                minWidth: '300px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                <div style={{ marginBottom: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Filter Lots for PDF</div>
                                    <Space>
                                        <Button size="small" type="link" onClick={selectAllLots}>
                                            Select All
                                        </Button>
                                        <Button size="small" type="link" onClick={deselectAllLots}>
                                            Deselect All
                                        </Button>
                                    </Space>
                                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
                                        {selectedLotIds.size} of {lastBiddersData?.lots?.length || 0} selected
                                    </div>
                                </div>
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                    {lastBiddersData?.lots?.map((lot) => (
                                        <Checkbox
                                            key={lot.id}
                                            checked={selectedLotIds.has(lot.id)}
                                            onChange={() => toggleLotSelection(lot.id)}
                                            style={{ width: '100%', padding: '4px 0' }}
                                        >
                                            <span>
                                                Lot #{lot.lot_number}: {lot.title?.substring(0, 40)}{lot.title?.length > 40 ? '...' : ''}
                                            </span>
                                        </Checkbox>
                                    ))}
                                </Space>
                            </div>
                        }
                        trigger="click"
                        open={filterDropdownVisible}
                        onOpenChange={setFilterDropdownVisible}
                        placement="topLeft"
                    >
                        <Button 
                            icon={<FilterOutlined />}
                            disabled={!lastBiddersData || lastBiddersLoading}
                        >
                            Filter Lots ({selectedLotIds.size}/{lastBiddersData?.lots?.length || 0})
                        </Button>
                    </Popover>,
                    <Button 
                        key="print" 
                        type="primary" 
                        icon={<FilePdfOutlined />}
                        onClick={() => {
                            if (selectedLotIds.size === 0) {
                                message.warning('Please select at least one lot to include in the PDF');
                                return;
                            }
                            handlePrint();
                        }}
                        disabled={!lastBiddersData || lastBiddersLoading || selectedLotIds.size === 0}
                    >
                        Save as PDF ({selectedLotIds.size})
                    </Button>
                ]}
                width={1400}
            >
                    {lastBiddersLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, minHeight: 400 }}>
                        <Spin size="large" tip="Loading bidders data..." />
                        </div>
                ) : (
                    <div ref={printRef}>
                        {lastBiddersData ? (
                        <div style={{ padding: '0 8px' }}>
                        {/* Cover Page - Print Only */}
                        {(() => {
                            const filteredLots = getFilteredLots();
                            return (
                                <div className="print-only cover-page">
                                    <div style={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        textAlign: 'center',
                                        background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 50%, #ffffff 100%)',
                                        borderRadius: 8,
                                        padding: '40px 20px'
                                    }}>
                                        {/* Logo */}
                                        <div style={{ marginBottom: 20 }}>
                                            <img 
                                                src="/assets/images/logo.png" 
                                                alt="Maveshi Bids Logo" 
                                                style={{ 
                                                    maxWidth: '200px', 
                                                    height: 'auto',
                                                    display: 'block',
                                                    margin: '0 auto'
                                                }}
                                                onError={(e) => {
                                                    console.error('Logo failed to load:', e.target.src);
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                        
                                        {/* Title */}
                                        <div style={{ fontSize: 28, fontWeight: 'bold', marginBottom: 12, color: '#262626' }}>
                                            Last 3 Bidders Report
                                        </div>
                                        
                                        {/* Auction Title */}
                                        <div style={{ fontSize: 18, color: '#595959', marginBottom: 6 }}>
                                            {lastBiddersData.auction?.title}
                                        </div>
                                        
                                        {/* Generated Date */}
                                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 20 }}>
                                            Generated on: {moment().format('YYYY-MM-DD HH:mm')}
                                        </div>
                                        
                                        {/* Report Created For */}
                                        {lastBiddersData.auction?.user && (
                                            <div style={{ fontSize: 12, color: '#595959', marginBottom: 24, lineHeight: 1.6 }}>
                                                Report generated for the auction owner: {lastBiddersData.auction.user.first_name || ''} {lastBiddersData.auction.user.last_name || ''}
                                                {lastBiddersData.auction.user.email && ` • ${lastBiddersData.auction.user.email}`}
                                                {lastBiddersData.auction.user.phone_number && ` • ${lastBiddersData.auction.user.phone_number}`}
                                            </div>
                                        )}
                                        
                                        {/* Summary Stats */}
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            gap: '30px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <div style={{ textAlign: 'center', minWidth: '120px' }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>Selected Lots</div>
                                                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#262626' }}>
                                                    {selectedLotIds.size || filteredLots.length}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'center', minWidth: '120px' }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>Lots with Bids</div>
                                                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#262626' }}>
                                                    {filteredLots.filter(lot => (lot.last_bidders || []).length > 0).length}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'center', minWidth: '120px' }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>Total Bidders</div>
                                                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#262626' }}>
                                                    {filteredLots.reduce((sum, lot) => sum + (lot.last_bidders || []).length, 0)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Screen Header - Visible on screen only */}
                        <div style={{ textAlign: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '2px solid #e8e8e8' }} className="no-print">
                            <div style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 8 }}>
                                Last 3 Bidders Report
                            </div>
                            <div style={{ fontSize: 18, color: '#595959', marginBottom: 4 }}>
                                {lastBiddersData.auction?.title}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                Generated on: {moment().format('YYYY-MM-DD HH:mm')}
                            </div>
                        </div>
                        {/* Summary Stats - Screen Only */}
                        {(() => {
                            const filteredLots = getFilteredLots();
                            return (
                                <Row gutter={[16, 16]} style={{ marginBottom: 24 }} className="no-print">
                                    <Col span={8}>
                                        <Card 
                                            size="small" 
                                            style={{ textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none' }}
                                        >
                                            <Statistic
                                                title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Selected Lots</span>}
                                                value={selectedLotIds.size || filteredLots.length}
                                                valueStyle={{ color: 'white', fontSize: 28 }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card 
                                            size="small" 
                                            style={{ textAlign: 'center', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', border: 'none' }}
                                        >
                                            <Statistic
                                                title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Lots with Bids</span>}
                                                value={filteredLots.filter(lot => (lot.last_bidders || []).length > 0).length}
                                                valueStyle={{ color: 'white', fontSize: 28 }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card 
                                            size="small" 
                                            style={{ textAlign: 'center', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', border: 'none' }}
                                        >
                                            <Statistic
                                                title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Bidders</span>}
                                                value={filteredLots.reduce((sum, lot) => sum + (lot.last_bidders || []).length, 0)}
                                                valueStyle={{ color: 'white', fontSize: 28 }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            );
                        })()}

                        {/* Lots Grid */}
                        <div className="lots-content">
                        <Row gutter={[16, 16]}>
                            {getFilteredLots().map((lot) => {
                                const bidders = lot.last_bidders || [];
                                return (
                                    <Col xs={24} sm={12} md={8} lg={8} key={lot.id}>
                                        <Card
                                            hoverable
                                            style={{
                                                borderRadius: 12,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s',
                                                height: '100%'
                                            }}
                                            title={
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <Tag color="blue" style={{ margin: 0, fontSize: 13, fontWeight: 'bold', padding: '2px 10px' }}>
                                                                Lot #{lot.lot_number}
                                                            </Tag>
                                                            {getLotStatusTag(lot.status)}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: 13, color: '#595959', fontWeight: 500, marginTop: 4 }}>
                                                        {lot.title}
                                                    </div>
                                                </div>
                                            }
                                            bodyStyle={{ padding: 16 }}
                                        >
                                            {bidders.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#bfbfbf' }}>
                                                    <UserOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} />
                                                    <div>No bids received</div>
                                                </div>
                                            ) : (
                                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                                    {bidders.map((b, idx) => {
                                                        const name = (b.user?.first_name || b.user?.last_name)
                                                            ? `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.trim()
                                                            : (b.user?.email || 'Unknown');
                                                        const winnerLabel = getWinnerLabel(idx);
                                                        const isWinner = idx === 0;
                                                        return (
                                                            <div key={idx} style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 12,
                                                                padding: '10px 12px',
                                                                borderRadius: 8,
                                                                border: `1px solid ${isWinner ? '#d9f7be' : '#e6f7ff'}`,
                                                                background: isWinner ? 'linear-gradient(135deg, #f6ffed 0%, #fafff0 100%)' : '#fafafa',
                                                                transition: 'all 0.2s'
                                                            }}>
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                                                                    {isWinner ? (
                                                                        <CrownOutlined style={{ fontSize: 20, color: '#faad14' }} />
                                                                    ) : (
                                                                        <Avatar size={32} icon={<UserOutlined />} style={{ backgroundColor: idx === 1 ? '#1890ff' : '#52c41a' }} />
                                                                    )}
                                                                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                                                        <span style={{ fontSize: 10, color: '#8c8c8c', lineHeight: 1.2, marginBottom: 2, fontWeight: 500 }}>
                                                                            {winnerLabel}
                                                                        </span>
                                                                        <span style={{ 
                                                                            fontSize: 14, 
                                                                            fontWeight: isWinner ? 'bold' : 'normal',
                                                                            color: isWinner ? '#389e0d' : '#262626',
                                                                            whiteSpace: 'nowrap', 
                                                                            overflow: 'hidden', 
                                                                            textOverflow: 'ellipsis'
                                                                        }}>
                                                                            {name}
                                                                        </span>
                                                                    </span>
                                                                </span>
                                                                <span style={{ textAlign: 'right', marginLeft: 'auto' }}>
                                                                    <div style={{ 
                                                                        fontWeight: 'bold', 
                                                                        fontSize: 15,
                                                                        color: isWinner ? '#389e0d' : '#262626',
                                                                        whiteSpace: 'nowrap' 
                                                                    }}>
                                                                        {formatCurrency(b.amount)}
                                                                    </div>
                                                                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                                                                        {moment(b.created_at).format('MMM DD, HH:mm')}
                                                                    </div>
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </Space>
                                            )}
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                        </div>

                        {/* Disclaimer Page - Print Only */}
                        <div className="print-only disclaimer-page">
                            <div style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                width: '100%',
                                maxWidth: '600px',
                                margin: '0 auto',
                                textAlign: 'center'
                            }}>
                                {/* Logo */}
                                <div style={{ marginBottom: 24 }}>
                                    <img 
                                        src="/assets/images/logo.png" 
                                        alt="Maveshi Bids Logo" 
                                        style={{ 
                                            maxWidth: '150px', 
                                            height: 'auto',
                                            display: 'block',
                                            margin: '0 auto'
                                        }}
                                        onError={(e) => {
                                            console.error('Logo failed to load:', e.target.src);
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                                
                                {/* Disclaimer Title */}
                                <div style={{ fontSize: 26, fontWeight: 'bold', marginBottom: 30, color: '#262626' }}>
                                    Confidentiality Notice
                                </div>
                                
                                {/* Disclaimer Content */}
                                <div style={{ 
                                    fontSize: 12, 
                                    color: '#595959', 
                                    lineHeight: 1.8,
                                    textAlign: 'left',
                                    marginBottom: 30,
                                    width: '100%'
                                }}>
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            CONFIDENTIAL AND PROPRIETARY INFORMATION
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            This report contains confidential and proprietary information belonging to <strong>Maveshi Bids</strong> and is intended solely for the use of the auction owner for whom it was generated. The information contained herein is strictly confidential and may not be disclosed, distributed, or shared with any third party without prior written consent from Maveshi Bids. This confidentiality obligation extends to all recipients, whether direct or indirect, and applies regardless of how the information is accessed or obtained.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            This includes but is not limited to: bidder names, contact information, bid amounts, auction details, lot information, pricing data, bidding patterns, timing information, payment history, communication records, and any other proprietary data contained within this document. All such information is considered trade secrets and proprietary business information of Maveshi Bids, protected under applicable intellectual property and trade secret laws.
                                        </div>
                                        <div>
                                            The confidential nature of this information is essential to the competitive advantage and business operations of Maveshi Bids. Any unauthorized disclosure could cause irreparable harm to Maveshi Bids, its business relationships, and the privacy rights of individuals whose information is contained within this report. Therefore, you are obligated to treat all information in this report with the highest degree of confidentiality and care.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            AUTHORIZED USE AND DISTRIBUTION
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            This report has been generated exclusively for the auction owner identified on the cover page. Unauthorized access, use, copying, or distribution of this document or any portion thereof is strictly prohibited and may violate applicable laws and regulations, including but not limited to data protection laws, privacy regulations, trade secret statutes, and contractual obligations.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            You are not permitted to: share this report via email, upload it to cloud storage services accessible by others, print multiple copies for distribution, post any content from this report on public forums or social media platforms, forward it to unauthorized individuals or entities, or use the information contained herein for any commercial purpose other than managing your auction.
                                        </div>
                                        <div>
                                            Furthermore, you may not allow any third party to access, view, copy, or use this report without explicit written authorization from Maveshi Bids. This restriction applies to employees, contractors, advisors, legal counsel, and any other individuals or entities who may have access to your facilities or systems. If you require assistance from third parties in reviewing this report, you must first obtain written consent from Maveshi Bids and ensure that such third parties agree to be bound by confidentiality obligations at least as restrictive as those set forth in this notice.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            DATA ACCURACY AND LIMITATIONS
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            While every effort has been made to ensure the accuracy of the information presented in this report, Maveshi Bids makes no warranties or representations regarding the completeness or accuracy of the data. The report is provided "as is" and may be subject to change without notice. Maveshi Bids does not guarantee that the information is current, complete, or error-free.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            The bidder information reflects the state of data at the time of report generation and may not reflect subsequent changes or updates. System delays, technical issues, network interruptions, server maintenance, or human error may result in inaccuracies, omissions, or delays in data reporting. Additionally, bidders may update their information, withdraw bids, or modify their participation status after the report generation date.
                                        </div>
                                        <div>
                                            You should verify critical information independently before making any business decisions based on this report. Maveshi Bids shall not be liable for any damages, including but not limited to direct, indirect, incidental, consequential, or punitive damages, resulting from reliance on this information. This limitation of liability applies regardless of the theory of liability, whether in contract, tort, strict liability, or otherwise, and even if Maveshi Bids has been advised of the possibility of such damages.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            INTELLECTUAL PROPERTY RIGHTS
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            All intellectual property rights in this report, including but not limited to the format, design, methodology, structure, compilation of data, analysis methods, presentation styles, algorithms, formulas, calculations, and proprietary techniques used to generate or present the information, remain the exclusive property of Maveshi Bids and are protected by copyright, trademark, trade secret, and other intellectual property laws.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            No part of this report may be reproduced, transmitted, stored in a retrieval system, translated into any language, or distributed in any form by any means (electronic, mechanical, photocopying, recording, or otherwise) without prior written authorization from Maveshi Bids. This includes but is not limited to creating derivative works, extracting data for use in other systems, or incorporating any portion of this report into other documents or presentations.
                                        </div>
                                        <div>
                                            Any unauthorized use of the intellectual property contained in this report may constitute copyright infringement, trademark infringement, misappropriation of trade secrets, or other violations of intellectual property laws, and may result in severe civil and criminal penalties. Maveshi Bids reserves all rights not expressly granted in this notice, and any use of this report beyond the scope expressly permitted herein is strictly prohibited.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            RETENTION AND DISPOSAL
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            You are advised to store this report securely and destroy it when it is no longer needed for business purposes. If storing electronically, ensure appropriate security measures are in place, including password protection, encryption, secure access controls, regular security audits, and protection against unauthorized access, data breaches, and cyberattacks. Physical copies should be stored in locked filing cabinets or secure storage facilities with restricted access.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            When disposing of this document, ensure it is completely destroyed and not recoverable. This includes securely deleting electronic copies using secure deletion methods that overwrite data multiple times, removing files from backup systems, and shredding physical copies using cross-cut shredders that produce small, unreadable fragments. Do not dispose of this report in public trash or recycling bins, and ensure that disposal contractors are aware of the confidential nature of the material.
                                        </div>
                                        <div>
                                            Failure to properly secure or dispose of this document may result in unauthorized access, data breaches, privacy violations, and potential legal liability. You are responsible for maintaining an audit trail of who has accessed this report and when, and you must notify Maveshi Bids immediately if you become aware of any unauthorized access, disclosure, or potential security breach involving this report.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            REPORT USAGE GUIDELINES
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            This report is intended for internal use by the auction owner only. Use the information contained herein responsibly and in compliance with all applicable privacy laws and regulations, including but not limited to data protection regulations, privacy legislation, industry-specific compliance requirements, and international data transfer restrictions. You must ensure that your use of this information complies with all applicable laws in your jurisdiction and any jurisdictions where the data subjects may be located.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            When referencing bidder information, ensure you comply with data protection regulations and respect the privacy rights of individuals. Do not use this information for marketing purposes, unsolicited communications, spam, harassment, discrimination, or any other purpose beyond the scope of managing your auction. The bidder data contained in this report is subject to privacy protections and should be handled accordingly, with appropriate safeguards to prevent unauthorized access, use, or disclosure.
                                        </div>
                                        <div>
                                            You must implement reasonable administrative, technical, and physical safeguards to protect the information in this report from unauthorized access, use, disclosure, alteration, or destruction. These safeguards should be appropriate to the sensitivity of the information and should include, but not be limited to, access controls, encryption, secure transmission protocols, and regular security assessments. Any security incidents or breaches involving this information must be reported to Maveshi Bids immediately.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            LEGAL COMPLIANCE AND ACKNOWLEDGMENT
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            By accessing this report, you acknowledge that you have read, understood, and agree to comply with all terms and conditions set forth in this confidentiality notice. Any violation of these terms may result in legal action, including but not limited to claims for damages, injunctive relief, recovery of legal costs, and criminal prosecution where applicable. Remedies available to Maveshi Bids may include monetary damages, injunctive relief to prevent further violations, recovery of costs and attorneys' fees, and any other remedies available under applicable law.
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            You agree to indemnify and hold harmless Maveshi Bids, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from your unauthorized use, disclosure, or misuse of this report or any information contained herein. This indemnification obligation will survive termination of your access to this report and will extend to any third-party claims that may arise from your breach of this confidentiality notice.
                                        </div>
                                        <div>
                                            In the event of a breach or threatened breach of this confidentiality notice, Maveshi Bids shall be entitled to seek and obtain injunctive relief without the necessity of proving actual damages or posting a bond, in addition to any other remedies available at law or in equity. You acknowledge that monetary damages alone may be insufficient to remedy a breach of this confidentiality notice and that injunctive relief may be necessary to protect Maveshi Bids' rights and interests.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            GOVERNING LAW AND JURISDICTION
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            This confidentiality notice shall be governed by and construed in accordance with the laws of the jurisdiction in which Maveshi Bids operates, without regard to its conflict of law provisions. Any disputes arising out of or relating to this confidentiality notice or the unauthorized use or disclosure of this report shall be subject to the exclusive jurisdiction of the courts in that jurisdiction.
                                        </div>
                                        <div>
                                            If any provision of this confidentiality notice is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid, illegal, or unenforceable provision shall be replaced with a valid, legal, and enforceable provision that comes closest to the intent of the original provision.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 22 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            TERMINATION AND SURVIVAL
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            Your right to access and use this report may be terminated by Maveshi Bids at any time, with or without cause or notice. Upon termination, you must immediately cease all use of this report and return or destroy all copies of the report and any extracts or summaries thereof.
                                        </div>
                                        <div>
                                            Notwithstanding any termination of your access to this report, all provisions of this confidentiality notice relating to confidentiality, intellectual property rights, indemnification, and limitation of liability shall survive and continue to bind you indefinitely. Your obligations under this confidentiality notice shall remain in effect even after you are no longer authorized to access or use this report.
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: 0 }}>
                                        <strong style={{ color: '#262626', display: 'block', marginBottom: 8, fontSize: 13 }}>
                                            CONTACT INFORMATION
                                        </strong>
                                        <div style={{ marginBottom: 10 }}>
                                            If you have questions regarding this report or need to request authorization for sharing or distributing any portion of this document, please contact Maveshi Bids through official channels. Any requests for data sharing or report distribution must be submitted in writing and approved prior to any such action. Unauthorized distribution or sharing will be considered a breach of this confidentiality agreement.
                                        </div>
                                        <div>
                                            For inquiries related to this report, including requests for clarification, authorization for sharing, or reporting security incidents, please contact Maveshi Bids through the official contact methods provided on the Maveshi Bids platform or website. All communications regarding this report should be marked as "Confidential" and handled with appropriate security measures to protect the information contained therein.
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Footer */}
                                <div style={{ 
                                    fontSize: 11, 
                                    color: '#8c8c8c', 
                                    marginTop: 40,
                                    paddingTop: 20,
                                    borderTop: '1px solid #d9d9d9',
                                    width: '100%'
                                }}>
                                    <div style={{ marginBottom: 6 }}>
                                        <strong style={{ color: '#595959', fontSize: 12 }}>Maveshi Bids</strong>
                                    </div>
                                    <div>
                                        Report Generated: {moment().format('MMMM DD, YYYY [at] HH:mm')}
                                    </div>
                                    <div style={{ marginTop: 6, fontStyle: 'italic' }}>
                                        This document is protected by copyright law and contains confidential information.
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 48 }}>
                            <Text type="secondary">No data available</Text>
                        </div>
                    )}
                </div>
                )}
                </Modal>

            {/* Auction Lots Modal */}
            <Modal
                title={
                    selectedAuctionForLots ? (
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
                                Lots for: {selectedAuctionForLots.title}
                            </div>
                            <div style={{ fontSize: 14, color: '#8c8c8c', fontWeight: 'normal' }}>
                                Manage featured status for lots
                            </div>
                        </div>
                    ) : 'Auction Lots'
                }
                open={lotsModalVisible}
                onCancel={() => {
                    setLotsModalVisible(false);
                    setSelectedAuctionForLots(null);
                    setAuctionLots([]);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setLotsModalVisible(false);
                        setSelectedAuctionForLots(null);
                        setAuctionLots([]);
                    }}>
                        Close
                    </Button>
                ]}
                width={1000}
            >
                <Table
                    loading={lotsLoading}
                    columns={[
                                    {
                                        title: 'Lot #',
                                        dataIndex: 'lot_number',
                                        key: 'lot_number',
                                        width: 80,
                                        render: (number) => (
                                            <Tag color="blue" style={{ fontWeight: 'bold' }}>
                                                #{number}
                                            </Tag>
                                        )
                                    },
                                    {
                                        title: 'Title',
                                        dataIndex: 'title',
                                        key: 'title',
                                        render: (text) => (
                                            <Text strong>{text}</Text>
                                        )
                                    },
                                    {
                                        title: 'Status',
                                        dataIndex: 'status',
                                        key: 'status',
                                        width: 100,
                                        render: (status) => (
                                            <Tag color={getStatusColor(status)}>
                                                {status.toUpperCase()}
                                            </Tag>
                                        )
                                    },
                                    {
                                        title: 'Starting Price',
                                        dataIndex: 'starting_price',
                                        key: 'starting_price',
                                        width: 120,
                                        render: (price) => formatCurrency(price)
                                    },
                                    {
                                        title: 'Featured',
                                        dataIndex: 'is_featured',
                                        key: 'is_featured',
                                        width: 120,
                                        render: (isFeatured, lot) => (
                                            <Button
                                                type={isFeatured ? 'primary' : 'default'}
                                                icon={isFeatured ? <StarFilled /> : <StarOutlined />}
                                                onClick={() => handleToggleLotFeatured(selectedAuctionForLots, lot)}
                                                loading={loadingLotId === lot.id}
                                                disabled={loadingLotId !== null && loadingLotId !== lot.id}
                                            >
                                                {isFeatured ? 'Featured' : 'Feature'}
                                            </Button>
                                        )
                                    }
                                ]}
                    dataSource={auctionLots}
                    rowKey="id"
                    locale={{
                        emptyText: <Empty description="No lots found for this auction" />
                    }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} lots`
                    }}
                    size="middle"
                />
            </Modal>
        </DashboardLayout>
    );
};

export default AdminAuctionManagement; 